<template>
  <div class="supplier-detail-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Back Button & Title -->
        <div class="d-flex align-center">
          <v-btn
            icon
            small
            class="back-btn mr-3"
            @click="$router.go(-1)"
            title="Go Back"
          >
            <v-icon color="#0f172a" size="20">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-domain</v-icon>
          </div>
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <span class="page-title">SUPPLIER DETAILS</span>
              <span class="supplier-code-badge" v-if="supplierDetail && supplierDetail.supplierCode">
                {{ supplierDetail.supplierCode }}
              </span>
            </div>
            <div class="page-subtitle">
              {{ supplierDetail && supplierDetail.supplierName ? supplierDetail.supplierName : 'Supplier Overview' }}
              <span v-if="supplierDetail && supplierDetail.contactNumber" class="ml-2">
                &bull; {{ supplierDetail.contactNumber }}
              </span>
              <span v-if="supplierDetail && supplierDetail.supplierLocation" class="ml-2">
                &bull; {{ supplierDetail.supplierLocation }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right: Action Buttons -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <v-btn
            outlined
            color="#0f172a"
            class="btn-secondary text-capitalize elevation-0"
            @click="goToSettlementHistory"
          >
            <v-icon left small color="#0f172a">mdi-history</v-icon>
            Settlement History
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="goToPurchaseSettlement"
          >
            <v-icon left small>mdi-scale-balance</v-icon>
            Purchase Settlement
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Supplier Overview: Left Identity Card + Right 4 Gold Metric Cards -->
    <div class="supplier-overview-grid mb-4">
      <!-- Left: Supplier Summary Card -->
      <v-card flat class="profile-card rounded-lg pa-4">
        <div class="d-flex align-start justify-space-between mb-3">
          <div class="d-flex align-center">
            <div class="supplier-avatar mr-3">
              {{ getInitials(supplierDetail.supplierName) }}
            </div>
            <div>
              <div class="profile-name">{{ supplierDetail.supplierName || '—' }}</div>
              <div class="profile-code text-muted">
                Code: <b class="text-slate-800">{{ supplierDetail.supplierCode || '—' }}</b>
              </div>
            </div>
          </div>
          <v-btn
            small
            outlined
            color="#9a6b27"
            class="view-more-pill-btn text-capitalize"
            @click="viewMore = true"
          >
            <v-icon x-small left>mdi-information-outline</v-icon>
            View More
          </v-btn>
        </div>

        <div class="profile-meta-list">
          <div class="meta-row">
            <span class="meta-label">
              <v-icon size="14" color="#64748b" class="mr-1">mdi-phone-outline</v-icon>
              Phone
            </span>
            <span class="meta-value">{{ supplierDetail.contactNumber || '—' }}</span>
          </div>

          <div class="meta-row">
            <span class="meta-label">
              <v-icon size="14" color="#64748b" class="mr-1">mdi-map-marker-outline</v-icon>
              Location
            </span>
            <span class="meta-value">{{ supplierDetail.supplierLocation || '—' }}</span>
          </div>

          <div class="meta-row" v-if="supplierDetail.GSTIN">
            <span class="meta-label">
              <v-icon size="14" color="#64748b" class="mr-1">mdi-file-certificate-outline</v-icon>
              GSTIN
            </span>
            <span class="meta-value font-family-mono">{{ supplierDetail.GSTIN }}</span>
          </div>
        </div>
      </v-card>

      <!-- Right: 4 Gold & Thankam Metric Cards -->
      <div class="kpi-grid">
        <!-- 1. Purchased Net Gold -->
        <div class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-label">PURCHASED NET GOLD</div>
            <div class="kpi-value">
              {{ formatWeight(supplierDetail.totalPurchasedGold) }} <span class="kpi-unit">gm</span>
            </div>
            <div class="kpi-hint">Total metal received</div>
          </div>
          <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
            <v-icon size="20" color="#2563eb">mdi-gold</v-icon>
          </div>
        </div>

        <!-- 2. Paid Thankam -->
        <div class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-label">PAID THANKAM</div>
            <div class="kpi-value" style="color: #16a34a;">
              {{ formatWeight(supplierDetail.totalSettledThankam) }} <span class="kpi-unit">gm</span>
            </div>
            <div class="kpi-hint">Settled pure thankam</div>
          </div>
          <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
            <v-icon size="20" color="#16a34a">mdi-check-decagram-outline</v-icon>
          </div>
        </div>

        <!-- 3. Unpaid Gold -->
        <div class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-label">UNPAID GOLD</div>
            <div class="kpi-value" :style="supplierDetail.totalPendingGold > 0 ? 'color: #dc2626;' : 'color: #0f172a;'">
              {{ formatWeight(supplierDetail.totalPendingGold) }} <span class="kpi-unit">gm</span>
            </div>
            <div class="kpi-hint">Pending balance metal</div>
          </div>
          <div class="kpi-icon-pill" style="background: #fef2f2; color: #dc2626;">
            <v-icon size="20" color="#dc2626">mdi-clock-alert-outline</v-icon>
          </div>
        </div>

        <!-- 4. Excess / Acidified Thankam -->
        <div class="kpi-card">
          <div class="kpi-content">
            <div class="kpi-label">EXCESS THANKAM</div>
            <div class="kpi-value">
              {{ formatWeight(supplierDetail.balanceThankam) }} <span class="kpi-unit">gm</span>
            </div>
            <div class="kpi-hint">
              Acidified: {{ supplierDetail.acidifiedThankam ? formatWeight(supplierDetail.acidifiedThankam) + ' gm' : '—' }}
            </div>
          </div>
          <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
            <v-icon size="20" color="#ca8a04">mdi-swap-horizontal-bold</v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Purchase History Master Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <!-- Section Bar -->
      <div class="section-table-header px-4 py-3 d-flex flex-wrap align-center justify-space-between border-b">
        <div class="d-flex align-center">
          <v-icon small color="#0f172a" class="mr-2">mdi-receipt-text-outline</v-icon>
          <span class="section-title">PURCHASE HISTORY</span>
          <span class="count-badge ml-3" v-if="totalCount > 0">
            {{ totalCount }} {{ totalCount === 1 ? 'Invoice' : 'Invoices' }}
          </span>
        </div>
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn icon small title="Refresh History" class="refresh-icon-btn" @click="getSupplierHistory">
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Purchase History Table -->
      <div v-if="supplierHistory && supplierHistory.length > 0">
        <v-simple-table class="pro-purchase-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 55px;">#</th>
                <th class="text-left" style="width: 125px;">Bill No</th>
                <th class="text-left" style="width: 140px;">Purchase Date</th>
                <th class="text-left" style="width: 130px;">Invoice No</th>
                <th class="text-right" style="width: 155px;">Purchased Gold (gm)</th>
                <th class="text-right" style="width: 155px;">Paid Thankam (gm)</th>
                <th class="text-right" style="width: 155px;">Unpaid Gold (gm)</th>
                <th class="text-center" style="width: 130px;">Status</th>
                <th class="text-center" style="width: 100px;">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, i) in supplierHistory" :key="value._id || i" class="table-row">
                <td class="text-muted font-weight-medium">
                  {{ page == 1 ? i + 1 : i + 1 + 20 * (page - 1) }}
                </td>
                <td>
                  <span class="code-pill">
                    #{{ value.billNo || '—' }}
                  </span>
                </td>
                <td>
                  <div class="d-flex align-center date-cell">
                    <v-icon size="14" color="#94a3b8" class="mr-1">mdi-calendar-blank-outline</v-icon>
                    <span>{{ formatDate(value.purchaseDate) }}</span>
                  </div>
                </td>
                <td>
                  <span class="invoice-no-text">{{ value.invoiceNo || '—' }}</span>
                </td>
                <td class="text-right font-weight-bold text-slate-900">
                  {{ formatWeight(value.totalNetWeight) }}
                </td>
                <td class="text-right font-weight-bold" style="color: #16a34a;">
                  {{ formatWeight(value.totalSettledWeight) }}
                </td>
                <td class="text-right font-weight-bold" :style="(value.totalUnSettledGoldWeight > 0) ? 'color: #dc2626;' : 'color: #64748b;'">
                  {{ formatWeight(value.totalUnSettledGoldWeight) }}
                </td>
                <td class="text-center">
                  <span :class="getStatusBadgeClass(value.settleStatus)">
                    {{ getStatusText(value.settleStatus) }}
                  </span>
                </td>
                <td class="text-center">
                  <v-btn
                    small
                    outlined
                    color="#0f172a"
                    class="view-action-btn"
                    @click="$router.push('/purchaseDetails?id=' + value._id)"
                    title="View Purchase Details"
                  >
                    <v-icon x-small left color="#0f172a">mdi-eye-outline</v-icon>
                    View
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Custom Pagination Bar -->
        <div class="table-pagination-bar px-4 py-3 d-flex flex-wrap align-center justify-space-between">
          <div class="pagination-info">
            Showing
            <span class="font-weight-bold text-slate-800">{{ showingFrom }}</span>
            to
            <span class="font-weight-bold text-slate-800">{{ showingTo }}</span>
            of
            <span class="font-weight-bold text-slate-800">{{ totalCount }}</span>
            records
          </div>

          <v-pagination
            v-if="Pagelength > 1"
            v-model="page"
            :length="Pagelength"
            :total-visible="7"
            circle
            color="#9a6b27"
            class="custom-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center pa-8">
        <div class="empty-icon-circle mb-3">
          <v-icon size="32" color="#94a3b8">mdi-receipt-text-outline</v-icon>
        </div>
        <div class="empty-title">No Purchase Invoices Recorded</div>
        <div class="empty-subtitle">
          There are no purchase invoices or thankam settlements recorded for this supplier yet.
        </div>
      </div>
    </v-card>

    <!-- Comprehensive Supplier Details Modal (View More) -->
    <v-dialog v-model="viewMore" max-width="820px" scrollable content-class="admin-supplier-dialog">
      <v-card class="rounded-lg modal-card">
        <!-- Modal Header -->
        <div class="modal-header d-flex align-center justify-space-between px-5 py-4 border-b">
          <div class="d-flex align-center">
            <div class="modal-header-icon mr-3">
              <v-icon color="#9a6b27" size="20">mdi-card-account-details-outline</v-icon>
            </div>
            <div>
              <div class="modal-title">Supplier Complete Profile</div>
              <div class="modal-subtitle">Full business, contact, tax &amp; banking credentials</div>
            </div>
          </div>
          <v-btn icon small @click="viewMore = false">
            <v-icon color="#64748b" size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-5 modal-content-wrap">
          <!-- Section 1: Business Identity & Contact -->
          <div class="detail-section mb-4">
            <div class="detail-section-title mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-domain</v-icon>
              Company &amp; Contact Details
            </div>
            <div class="detail-grid-3">
              <div class="detail-item">
                <span class="detail-label">Supplier Name</span>
                <span class="detail-val font-weight-bold">{{ supplierDetail.supplierName || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Supplier Code</span>
                <span class="detail-val font-family-mono">{{ supplierDetail.supplierCode || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Mobile Number</span>
                <span class="detail-val">{{ supplierDetail.contactNumber || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Email Address</span>
                <span class="detail-val">{{ supplierDetail.email || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Location / City</span>
                <span class="detail-val">{{ supplierDetail.supplierLocation || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Company ID</span>
                <span class="detail-val">{{ supplierDetail.companyId || '—' }}</span>
              </div>
              <div class="detail-item span-full">
                <span class="detail-label">Full Address</span>
                <span class="detail-val">{{ supplierDetail.address || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Section 2: Tax & Compliance -->
          <div class="detail-section mb-4">
            <div class="detail-section-title mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-file-certificate-outline</v-icon>
              Tax &amp; Compliance Identifiers
            </div>
            <div class="detail-grid-3">
              <div class="detail-item">
                <span class="detail-label">GSTIN</span>
                <span class="detail-val font-family-mono">{{ supplierDetail.GSTIN || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">PAN Number</span>
                <span class="detail-val font-family-mono">{{ supplierDetail.pancardNo || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">TAN Number</span>
                <span class="detail-val font-family-mono">{{ supplierDetail.TAN || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">BIS License</span>
                <span class="detail-val">{{ supplierDetail.BisLicense || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Policy Number</span>
                <span class="detail-val">{{ supplierDetail.policyNo || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Section 3: Banking Credentials -->
          <div class="detail-section">
            <div class="detail-section-title mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-bank-outline</v-icon>
              Bank Account Details
            </div>
            <div class="detail-grid-3">
              <div class="detail-item">
                <span class="detail-label">Bank Name</span>
                <span class="detail-val">{{ supplierDetail.bankName || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Branch</span>
                <span class="detail-val">{{ supplierDetail.branch || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Account Number</span>
                <span class="detail-val font-family-mono font-weight-bold">{{ supplierDetail.accountNo || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">IFSC Code</span>
                <span class="detail-val font-family-mono">{{ supplierDetail.IFSCCode || '—' }}</span>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Modal Actions -->
        <div class="modal-footer px-5 py-3 d-flex justify-end border-t">
          <v-btn
            small
            outlined
            color="#0f172a"
            class="text-capitalize px-4"
            @click="viewMore = false"
          >
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SupplierDetail",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      page: 1,
      Pagelength: 1,
      totalCount: 0,
      supplierDetail: {},
      supplierHistory: [],
      viewMore: false,
    };
  },
  mounted() {
    this.getSupplier();
    this.getSupplierHistory();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    showingFrom() {
      if (!this.totalCount || this.totalCount === 0) return 0;
      return (this.page - 1) * 20 + 1;
    },
    showingTo() {
      if (!this.totalCount) return this.supplierHistory ? this.supplierHistory.length : 0;
      return Math.min(this.page * 20, this.totalCount);
    },
  },
  watch: {
    page() {
      this.getSupplierHistory();
    },
  },
  methods: {
    getSupplier() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/supplier/view",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.supplierDetail = response.data.data || {};
          } else {
            this.showsnackbar = true;
            this.msg = response.data ? response.data.msg : "Failed to load supplier details";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error loading supplier details:", err);
        });
    },
    getSupplierHistory() {
      this.appLoading = true;
      axios({
        method: "post",
        url: "/supplier/purchase/report",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          page: this.page,
          count: 20,
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.supplierHistory = response.data.data || [];
            this.totalCount = response.data.count || this.supplierHistory.length;
            this.Pagelength = Math.max(1, response.data.pages || Math.ceil(this.totalCount / 20));
          } else {
            this.showsnackbar = true;
            this.msg = response.data ? response.data.msg : "Failed to load purchase history";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error loading supplier history:", err);
        });
    },
    goToPurchaseSettlement() {
      const name = this.supplierDetail && this.supplierDetail.supplierName ? this.supplierDetail.supplierName : "";
      this.$router.push("/supplierSettlement?id=" + this.$route.query.id + "&&name=" + encodeURIComponent(name));
    },
    goToSettlementHistory() {
      this.$router.push("/settledList?id=" + this.$route.query.id);
    },
    formatWeight(wt) {
      if (wt === undefined || wt === null || isNaN(wt)) return "0.00";
      const val = Number(wt);
      return val.toFixed(2);
    },
    formatDate(item) {
      if (!item) return "-";
      try {
        const dt = new Date(item);
        if (isNaN(dt.getTime())) return "-";
        const day = dt.getDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[dt.getMonth()];
        const year = dt.getFullYear();
        return `${day} ${month} ${year}`;
      } catch (e) {
        return "-";
      }
    },
    getInitials(name) {
      if (!name) return "S";
      const trimmed = name.trim();
      if (!trimmed) return "S";
      const parts = trimmed.split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return trimmed.substring(0, 2).toUpperCase();
    },
    getStatusText(status) {
      if (!status) return "Not Settled";
      if (status === "Settled") return "Settled";
      if (status === "partiallySettled") return "Partially Settled";
      if (status === "notSettled") return "Not Settled";
      return status;
    },
    getStatusBadgeClass(status) {
      if (status === "Settled") return "status-badge-settled";
      if (status === "partiallySettled") return "status-badge-partial";
      return "status-badge-unsettled";
    },
  },
};
</script>

<style scoped>
.supplier-detail-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.back-btn {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.back-btn:hover {
  background: #f8fafc;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.supplier-code-badge {
  font-family: monospace;
  font-weight: 700;
  font-size: 11px;
  background: #f1f5f9;
  color: #0f172a;
  padding: 2px 7px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

.btn-secondary {
  border-radius: 8px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-color: #cbd5e1 !important;
  background: #ffffff !important;
  transition: all 0.2s ease !important;
}

.btn-secondary:hover {
  background: #f8fafc !important;
  border-color: #94a3b8 !important;
}

/* Supplier Overview Grid: Left Profile Card + Right 4 KPI Cards */
.supplier-overview-grid {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 12px;
}

@media (max-width: 1024px) {
  .supplier-overview-grid {
    grid-template-columns: 1fr;
  }
}

.profile-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.supplier-avatar {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1;
  color: #0f172a;
  font-weight: 800;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.profile-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.profile-code {
  font-size: 11.5px;
  margin-top: 2px;
}

.view-more-pill-btn {
  font-size: 11px !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  height: 28px !important;
  padding: 0 8px !important;
  border-color: #e2e8f0 !important;
}

.view-more-pill-btn:hover {
  background: #fefce8 !important;
}

.profile-meta-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f8fafc;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
}

.meta-label {
  color: #64748b;
  display: flex;
  align-items: center;
}

.meta-value {
  color: #0f172a;
  font-weight: 600;
}

/* 4 KPI Metrics Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1280px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Master Purchase Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.section-table-header {
  background: #f8fafc;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.count-badge {
  font-size: 11px;
  background: #e2e8f0;
  color: #475569;
  padding: 2px 7px;
  border-radius: 12px;
  font-weight: 600;
}

.refresh-icon-btn {
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  height: 32px !important;
  width: 32px !important;
}

.refresh-icon-btn:hover {
  background: #f8fafc !important;
}

.pro-purchase-table :deep(table) {
  width: 100%;
  border-collapse: collapse;
}

.pro-purchase-table :deep(th) {
  background-color: #ffffff !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}

.pro-purchase-table :deep(td) {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.pro-purchase-table :deep(tr:hover) {
  background-color: #f8fafc !important;
}

.code-pill {
  font-family: monospace;
  font-weight: 700;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: #0f172a;
  letter-spacing: 0.3px;
}

.date-cell {
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
}

.invoice-no-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 12.5px;
}

.status-badge-settled {
  font-size: 10.5px;
  font-weight: 700;
  background: #dcfce7;
  color: #15803d;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #bbf7d0;
}

.status-badge-partial {
  font-size: 10.5px;
  font-weight: 700;
  background: #ffedd5;
  color: #c2410c;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #fed7aa;
}

.status-badge-unsettled {
  font-size: 10.5px;
  font-weight: 700;
  background: #f1f5f9;
  color: #64748b;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.view-action-btn {
  border-radius: 6px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: capitalize !important;
  border-color: #cbd5e1 !important;
}

.view-action-btn:hover {
  background: #f8fafc !important;
  border-color: #94a3b8 !important;
}

/* Pagination Bar */
.table-pagination-bar {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.pagination-info {
  font-size: 12.5px;
  color: #64748b;
}

.custom-pagination :deep(.v-pagination__item) {
  font-size: 12px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
}

.custom-pagination :deep(.v-pagination__navigation) {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Modal Styling */
.modal-card {
  background: #ffffff !important;
}

.modal-header {
  background: #f8fafc;
}

.modal-header-icon {
  background: #fefce8;
  border: 1px solid #fef08a;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
}

.detail-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 16px;
}

.detail-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
}

.detail-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 768px) {
  .detail-grid-3 {
    grid-template-columns: 1fr;
  }
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item.span-full {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 2px;
}

.detail-val {
  font-size: 13px;
  color: #0f172a;
}

.modal-footer {
  background: #f8fafc;
}

/* Empty State */
.empty-state-wrap {
  padding: 50px 20px;
}

.empty-icon-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 4px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.border-b {
  border-bottom: 1px solid #e2e8f0;
}

.border-t {
  border-top: 1px solid #e2e8f0;
}

.font-family-mono {
  font-family: monospace;
}

.text-muted {
  color: #94a3b8;
}

.text-slate-800 {
  color: #1e293b;
}

.text-slate-900 {
  color: #0f172a;
}

@media (min-width: 1264px) {
  .admin-supplier-dialog {
    margin-left: calc(100vw / 12) !important;
    margin-right: auto !important;
  }
}
</style>